import { memo } from "react";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { TableGridMode } from "consts/table";
import { Filters, MenuItem } from "consts/openBanking";
import { FilterVariant } from "consts/filters";
import type { BankingSdkConnector } from "features/openBanking/types";
import {
    selectOpenBankingResourceSlice,
    selectIsOpenBankingResourceLoading,
    selectIsOpenBankingResourceUninitialized
} from "features/openBanking/selectors";
import { massUpdateBankingSdkConnectors } from "features/openBanking/api";
import BankingSDKToolbar from "./BankingSDKToolbar";
import BankingSDKDetail from "./BankingSDKDetail";
import useBankingSDK from "./useBankingSDK";
import {
    OpenBankingResource,
    LogoUrl,
    MassUpdateOpenBankingResource,
    useOpenBankingResourceSelectionModel
} from "../shared";

const BankingSDK = () => {
    const {
        getCellProps,
        onChange
    } = useBankingSDK();

    const [selectionModel, onSelectionModelChange] = useOpenBankingResourceSelectionModel();

    return (
        <OpenBankingResource
            BreadcrumbsProps={{
                heading: 'Banking SDK Connectors',
                action: (
                    <MassUpdateOpenBankingResource
                        selectionModel={selectionModel}
                        onSelectionModelChange={onSelectionModelChange}
                        onConfirm={iso2Countries => massUpdateBankingSdkConnectors({
                            countryISO: iso2Countries.join(','),
                            connectorIds: selectionModel.join(',')
                        })}
                        ModalProps={{
                            titleSlot: 'Mass Update Banking SDK Connectors'
                        }}
                    >
                        Mass Update Banking SDK Connectors
                    </MassUpdateOpenBankingResource>
                )
            }}
            ResourceProps={{
                disableSelectionOnClick: true,
                checkboxSelection: true,
                name: 'Banking SDK Connectors',
                useResource: useBankingSDK,
                mode: TableGridMode.OpenBankingBankingSDK,
                selectionModel,
                onSelectionModelChange,
                columns: [
                    {
                        field: Filters.logoUrl,
                        headerName: 'Logo',
                        minWidth: 160,
                        renderCell: (params: GridRenderCellParams<BankingSdkConnector['logoUrl']>) => (
                            <LogoUrl
                                {...params}
                            />
                        ),
                        headerAlign: 'center',
                        align: 'center'
                    }
                ],
                selectIsTableUninitialized: selectIsOpenBankingResourceUninitialized(MenuItem.BankingSDK),
                selectIsTableLoading: selectIsOpenBankingResourceLoading(MenuItem.BankingSDK),
                selectTableDataSlice: selectOpenBankingResourceSlice(MenuItem.BankingSDK),
                Toolbar: BankingSDKToolbar,
                Detail: BankingSDKDetail,
                CellProps: {
                    [Filters.isEnabled]: {
                        onChange,
                        getCellProps,
                    },
                    [Filters.shouldCreditOnRedirect]: {
                        onChange,
                        getCellProps
                    }
                },
                filterTypes: new Map<FilterVariant, string[]>([
                    [FilterVariant.MultiOption, [Filters.countryISO]],
                    [FilterVariant.SingleOption, [Filters.isEnabled]],
                ])
            }}
        />
    );
};

export default memo(BankingSDK);
